import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';

type NoteItemFormProps = {
  label: string;
  initialText?: string;
  submitLabel: string;
  isPending: boolean;
  onSubmit: (text: string) => void;
  onCancel: () => void;
};

/**
 * The text of one note while the user writes or changes it. Save needs a text that is not blank
 * and differs from the initial one. Escape discards, Cmd/Ctrl+Enter saves, Enter breaks the line.
 */
export const NoteItemForm = ({
  label,
  initialText = '',
  submitLabel,
  isPending,
  onSubmit,
  onCancel,
}: NoteItemFormProps) => {
  const [text, setText] = useState(initialText);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const trimmedText = text.trim();
  const dirty = trimmedText !== initialText.trim();
  const canSubmit = dirty && trimmedText !== '' && !isPending;

  useEffect(() => textAreaRef.current?.focus(), []);

  const submit = () => {
    if (canSubmit) {
      onSubmit(trimmedText);
    }
  };

  return (
    <YStack className="w-full gap-2">
      <TextArea
        ref={textAreaRef}
        aria-label={label}
        value={text}
        disabled={isPending}
        className="min-h-20 text-sm [field-sizing:content]"
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          // Enter or Escape while composing (IME) edits the composed text
          if (event.nativeEvent.isComposing) return;
          if (event.key === 'Escape') onCancel();
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey))
            submit();
        }}
      />
      <XStack className="justify-end gap-2">
        <Button
          variant="ghost"
          size="dense"
          disabled={isPending}
          onClick={onCancel}
        >
          Discard
        </Button>
        <Button
          variant="default"
          size="dense"
          disabled={!canSubmit}
          isLoading={isPending}
          loadingText="Saving…"
          onClick={submit}
        >
          {submitLabel}
        </Button>
      </XStack>
    </YStack>
  );
};
